<template>
    <section aria-label="Redemption payment details">
        <h2 class="text-slate-800 dark:text-slate-100 text-base font-medium">Price</h2>
        <p class="text-slate-500 dark:text-slate-400 text-sm mb-4">
            Please specify the advertised cash price of your redemption.
        </p>
        <div class="grid grid-cols-2 gap-4 mb-2">
            <div>
                <Input
                    label="Cash cost"
                    placeholder="Cash cost"
                    name="advertised_price"
                    id="cash-cost"
                    :errors="tripFormErrors"
                >
                    <template #input>
                        <div class="flex">
                            <div
                                class="flex items-center justify-center px-3 rounded-l border text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 focus-within:border-slate-800"
                            >
                                <TripIcon
                                    icon="dollar"
                                    class="text-slate-500 dark:text-slate-400"
                                    height="16"
                                    width="16"
                                />
                            </div>
                            <input
                                id="cash-cost"
                                class="w-full h-10 text-sm rounded-r border-t border-b border-r border-l-0 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 px-3 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors focus:!ring-0 focus:!ring-offset-0 focus:!outline-none focus:!border-slate-800 focus-visible:!ring-0 focus-visible:!outline-none active:!ring-0 active:!outline-none hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                type="text"
                                v-decimal-format
                                placeholder="Cash cost"
                                v-model="activeRedemption.advertised_price"
                            />
                        </div>
                    </template>
                </Input>
            </div>
            <div>
                <Input
                    label="Taxes & fees"
                    placeholder="Taxes & fees"
                    name="advertised_taxes_fees"
                    id="taxes-fees"
                    :errors="tripFormErrors"
                >
                    <template #input>
                        <div class="flex">
                            <div
                                class="flex items-center justify-center px-3 rounded-l border text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 focus-within:border-slate-800"
                            >
                                <TripIcon
                                    icon="dollar"
                                    class="text-slate-500 dark:text-slate-400"
                                    height="16"
                                    width="16"
                                />
                            </div>
                            <input
                                id="taxes-fees"
                                class="w-full h-10 text-sm rounded-r border-t border-b border-r border-l-0 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 px-3 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors focus:!ring-0 focus:!ring-offset-0 focus:!outline-none focus:!border-slate-800 focus-visible:!ring-0 focus-visible:!outline-none active:!ring-0 active:!outline-none hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                type="text"
                                v-decimal-format
                                placeholder="Taxes & fees"
                                v-model="activeRedemption.advertised_taxes_fees"
                            />
                        </div>
                    </template>
                </Input>
            </div>
        </div>

        <div class="flex justify-end items-center mb-6">
            <p class="text-slate-500 dark:text-slate-400 text-xs font-normal leading-4">
                Total: {{ formattedTotalPrice }}
                <span
                    v-if="
                        isUsingCompanionPass &&
                        activeRedemption.isSouthwestFlight
                    "
                    class="text-red-500"
                >
                    (incl. approx. {{ formattedCompanionTax }} tax for
                    companion)
                </span>
            </p>
        </div>

        <h2 class="text-slate-800 dark:text-slate-100 text-base font-medium">Payment</h2>
        <p class="text-slate-500 dark:text-slate-400 text-sm mb-4">
            Please specify how you paid for this redemption. This will help us
            calculate the value of your redemptions.
        </p>
        <h3 class="text-slate-800 dark:text-slate-100 text-base font-medium mb-1">Pay with</h3>

        <fieldset
            class="flex gap-2 border-none sm:flex-row flex-col"
            :class="{
                'p-2 border border-red-500 rounded-lg':
                    tripFormErrors?.payment_type,
            }"
            aria-labelledby="payment-type-legend"
        >
            <legend id="payment-type-legend" class="sr-only">
                Payment type
            </legend>
            <label
                class="w-full md:w-[48%] gap-2 relative rounded-lg border-2 sm:flex-col items-center sm:justify-center p-3 hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors cursor-pointer max-sm:flex"
                :class="{
                    'border-orange-500':
                        paymentType.value === activeRedemption.payment_type,
                    'border-slate-200 dark:border-slate-700':
                        paymentType.value !== activeRedemption.payment_type,
                }"
                v-for="paymentType in paymentTypes"
                :key="paymentType.value"
            >
                <input
                    type="radio"
                    v-model="activeRedemption.payment_type"
                    :value="paymentType.value"
                    class="sr-only"
                />
                <div class="w-6 h-6 sm:mb-2 text-slate-400">
                    <TripIcon :icon="paymentType.icon" />
                </div>
                <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                    {{ paymentType.label }}
                </span>
            </label>
        </fieldset>
        <div
            v-if="tripFormErrors?.payment_type"
            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
            role="alert"
        >
            <div class="flex items-center justify-center">
                <Icon icon="caution" />
            </div>
            {{ tripFormErrors.payment_type }}
        </div>

        <!-- Other Payment Details Input -->
        <div v-if="activeRedemption.payment_type === 'other'" class="pt-4">
            <Textarea
                label="Payment details"
                placeholder="Add payment details"
                name="other_payment_details"
                id="other-payment-details"
                :errors="tripFormErrors"
            >
                <template #input>
                    <textarea
                        id="other-payment-details"
                        class="w-full text-sm rounded border border-slate-200 dark:border-slate-700 px-3"
                        placeholder="Add payment details"
                        rows="4"
                        v-model="activeRedemption.other_payment_details"
                    ></textarea>
                </template>
            </Textarea>
        </div>

        <div class="pt-6">
            <div
                class="grid grid-cols-2 gap-4 mb-6"
                v-if="
                    activeRedemption.payment_type === 'cash_points' ||
                    activeRedemption.payment_type === 'points'
                "
                aria-label="Payment details"
            >
                <div v-if="activeRedemption.payment_type === 'cash_points'">
                    <Input
                        label="Cash used"
                        placeholder="Cash used"
                        id="payment-cash-cost"
                        name="payment_cash_amount"
                        :errors="tripFormErrors"
                    >
                        <template #input>
                            <div class="flex">
                                <div
                                    class="flex items-center justify-center bg-white dark:bg-slate-800 px-3 rounded-l border text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700"
                                    :class="{
                                        '!border-red-500':
                                            tripFormErrors?.payment_cash_amount,
                                    }"
                                >
                                    <TripIcon
                                        icon="dollar"
                                        class="text-slate-500 dark:text-slate-400"
                                        height="16"
                                        width="16"
                                    />
                                </div>
                                <input
                                    id="payment-cash-cost"
                                    class="w-full h-10 text-sm rounded-r border-t border-b border-r border-l-0 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 px-3 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors focus:ring-0 focus:ring-offset-0 focus:outline-none focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                    :class="{
                                        '!border-red-500':
                                            tripFormErrors?.payment_cash_amount,
                                    }"
                                    type="text"
                                    v-decimal-format
                                    placeholder="Cash cost"
                                    v-model="
                                        activeRedemption.payment_cash_amount
                                    "
                                />
                            </div>
                        </template>
                    </Input>
                </div>
                <div
                    v-if="
                        activeRedemption.payment_type === 'cash_points' ||
                        activeRedemption.payment_type === 'points'
                    "
                >
                    <Input
                        label="Taxes & fees"
                        placeholder="Taxes & fees"
                        id="payment-taxes-fees"
                        name="payment_taxes_fees"
                        :errors="tripFormErrors"
                    >
                        <template #input>
                            <div class="flex">
                                <div
                                    class="flex items-center justify-center bg-white dark:bg-slate-800 px-3 rounded-l border text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 focus-within:border-slate-800"
                                    :class="{
                                        '!border-red-500':
                                            tripFormErrors?.payment_taxes_fees,
                                    }"
                                >
                                    <TripIcon
                                        icon="dollar"
                                        class="text-slate-500 dark:text-slate-400"
                                        height="16"
                                        width="16"
                                    />
                                </div>
                                <input
                                    id="payment-taxes-fees"
                                    class="w-full h-10 text-sm rounded-r border-t border-b border-r border-l-0 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 px-3 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors focus:ring-0 focus:ring-offset-0 focus:outline-none focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                    pattern="\d*"
                                    :class="{
                                        '!border-red-500':
                                            tripFormErrors?.payment_taxes_fees,
                                    }"
                                    type="text"
                                    placeholder="Taxes & fees"
                                    v-decimal-format
                                    v-model="
                                        activeRedemption.payment_taxes_fees
                                    "
                                />
                            </div>
                        </template>
                    </Input>
                </div>
            </div>

            <div
                class="grid grid-cols-2 gap-4 mb-6"
                v-if="
                    activeRedemption.payment_type === 'points' ||
                    activeRedemption.payment_type === 'cash_points'
                "
                aria-label="Points details"
            >
                <div>
                    <Input
                        label="Points used"
                        name="payment_points_used"
                        placeholder="Points used"
                        id="payment-points-used"
                        v-model:inputValue="activeRedemption.payment_points"
                        :errors="tripFormErrors"
                        :inputFormat="'thousands'"
                        :pattern="'/[0-9]+/'"
                        :options="{
                            inputClass: 'h-10 text-sm text-slate-500 dark:text-slate-400',
                        }"
                    />
                </div>
                <div>
                    <h6
                        class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
                        for="points-currency"
                    >
                        Points currency
                    </h6>
                    <ProgramsDropdown />
                </div>
            </div>

            <aside
                class="flex p-3 items-start gap-3 self-stretch rounded-lg bg-slate-50 dark:bg-slate-900/40 mb-6 text-slate-500 dark:text-slate-400"
                v-if="activeRedemption.payment_type === 'cash'"
                aria-label="Cash payment information"
            >
                <Icon icon="circle-info" aria-hidden="true" />
                <div
                    class="flex flex-col justify-center items-start gap-1 flex-1"
                >
                    <p class="text-sm font-medium leading-5">
                        Total cash payments are calculated from the prices
                        entered above:
                    </p>
                    <p class="text-sm font-normal leading-5">
                        {{ formattedCashPrice }} + {{ formattedTaxesPrice }}
                        <span
                            v-if="
                                isUsingCompanionPass &&
                                activeRedemption.isSouthwestFlight
                            "
                            class="text-red-500"
                        >
                            (incl. {{ formattedCompanionTax }} tax for
                            companion)
                        </span>
                        = {{ formattedTotalPrice }}
                    </p>
                </div>
            </aside>

            <aside
                v-if="flexibleCurrencyTransferConflict"
                class="flex p-3 items-start gap-3 self-stretch rounded-lg bg-amber-50 dark:bg-amber-900/20 mb-4 text-amber-700 dark:text-amber-400"
                aria-label="Flexible currency transfer warning"
            >
                <Icon icon="circle-info" aria-hidden="true" />
                <div
                    class="flex flex-col justify-center items-start gap-1 flex-1"
                >
                    <p class="text-sm font-medium leading-5">
                        This redemption has transfers recorded into a flexible
                        currency, which isn't normally supported.
                    </p>
                    <p class="text-sm font-normal leading-5">
                        Saving with the transfer toggle off will remove the
                        recorded transfers. Leave it on to keep them.
                    </p>
                </div>
            </aside>

            <div
                class="grid grid-cols-4 gap-4 mb-6 w-full"
                v-if="showFlexibleCurrencyTransfer"
                aria-label="Flexible currency transfer option"
            >
                <div class="col-span-3">
                    <label for="flexible-points-transfer">
                        <p class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Points were transferred in from a flexible currency
                        </p>
                        <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                            Transfer from one reward program to another.
                        </p>
                    </label>
                </div>
                <div class="col-span-1 flex items-center justify-end">
                    <Switch
                        label="Transferring points"
                        v-model:value="
                            activeRedemption.flexible_points_transfer
                        "
                        @input="togglePointsTransferred"
                    />
                </div>
            </div>
            <!-- start redemption transfer form: -->
            <transition name="fade">
                <div
                    class="mb-6 rounded flex flex-col gap-4"
                    v-if="
                        activeRedemption.flexible_points_transfer &&
                        activeRedemption.payment_type !== 'cash'
                    "
                    aria-label="Point transfers"
                >
                    <TransitionGroup name="fade">
                        <div
                            v-for="(transfer, i) in activeRedemption.transfers"
                            :key="transfer"
                            class="rounded border"
                            :class="{
                                'border-red-500': tripFormErrors?.transfers,
                                'border-transparent':
                                    !tripFormErrors?.transfers,
                            }"
                        >
                            <RedemptionTransfer
                                :transfer="transfer"
                                :index="i"
                            />
                        </div>
                    </TransitionGroup>

                    <button
                        @click="addTransfer"
                        class="mt-4 flex rounded justify-start items-center gap-2 text-slate-800 dark:text-slate-100 focus:outline-none active:outline-none hover:border-slate-800 focus:ring-2 focus:ring-slate-200"
                        type="button"
                    >
                        <Icon icon="plus" class="h-6" aria-hidden="true" />
                        <span class="text-sm font-medium">
                            Add points transfer
                        </span>
                    </button>
                </div>
            </transition>
            <!-- end redemption transfer form -->
            <!-- start SW Companion Pass switch -->
            <div
                v-if="
                    activeRedemption.isSouthwestFlight &&
                    activeRedemption.payment_type
                "
                class="mb-6 flex items-center gap-8"
                aria-label="Southwest Companion Pass option"
            >
                <div
                    class="flex flex-col justify-center items-start gap-1 flex-1"
                >
                    <h3 class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                        I used the Southwest Companion Pass
                    </h3>
                    <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                        Switch this on if you used your Southwest Companion Pass
                        to book a free flight for your companion. We'll assume
                        the companion pass taxes are included in the "Taxes &
                        Fees" field above.
                    </p>
                </div>
                <div>
                    <Switch
                        :value="isUsingCompanionPass"
                        @input="toggleCompanionPass"
                        label="Use Companion Pass"
                    />
                </div>
            </div>
            <!-- end SW Companion Pass switch -->

            <!-- Travel Portal Selector -->
            <TravelPortalSelector
                v-if="activeRedemption.payment_type"
                :redemption="activeRedemption"
                :errors="tripFormErrors"
                @update:travelPortal="updateTravelPortal"
                @update:travelPortalName="updateTravelPortalName"
                @update:usedTravelPortal="updateUsedTravelPortal"
            />

            <div class="grid grid-cols-2 gap-4 mb-6">
                <Input
                    label="Booking date"
                    placeholder="Booking date"
                    id="booking-date"
                    :options="{
                        icon: 'calendar',
                        iconPosition: 'left',
                    }"
                >
                    <template #input>
                        <FlatPickr
                            :config="dateConfig"
                            v-model="activeRedemption.booking_date"
                            name="booking_date"
                            id="booking-date"
                            class="h-10 relative block w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pl-10 text-gray-900 dark:text-gray-100 placeholder:text-slate-500 dark:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                            placeholder="Booking date"
                        />
                    </template>
                </Input>
            </div>

            <div>
                <h6
                    id="connected-account"
                    class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
                    aria-label="Credit card used"
                >
                    Credit card used
                </h6>
                <CreditCardDropdown />
            </div>
        </div>
    </section>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import { ref, watch, computed, onMounted } from 'vue';
import { useTripsStore } from '@/store/trips';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import Textarea from '@/components/ui/inputs/ui-textarea.vue';
import Switch from '@/components/ui/switch/switch.vue';
import TripIcon from '@/components/trips/icons.vue';
import ProgramsDropdown from '@/components/trips/modals/steps/programs-dropdown.vue';
import CreditCardDropdown from '@/components/trips/modals/steps/credit-card-dropdown.vue';
import RedemptionTransfer from '@/components/trips/modals/steps/redemption-transfers.vue';
import TravelPortalSelector from '@/components/trips/modals/travel-portal-selector.vue';

const tripsStore = useTripsStore();
const { activeRedemption, paymentTypes, tripFormErrors, program } =
    storeToRefs(tripsStore);
const { transferPartnersGuideEnabled } = useFeatureFlags();

// MY1-3014: id-less placeholder rows (resetActiveRedemptionData seeds one)
// don't count — only rows that exist in the database.
const hasPersistedTransfers = computed(() =>
    (activeRedemption.value.transfers ?? []).some((transfer) => transfer.id)
);

const isPointsRedemption = computed(
    () =>
        activeRedemption.value.payment_type === 'points' ||
        activeRedemption.value.payment_type === 'cash_points'
);

// A saved redemption that violates the flexible-currency rule: transfers
// recorded into a flexible destination. Keyed on persisted rows, not the
// flexible_points_transfer flag — the backend preserves rows through a flag
// flip, so flag=false with surviving rows is a reachable state that must
// stay visible. Gated on isPointsRedemption so the warning never renders
// while the toggle section it refers to is hidden (e.g. cash redemptions).
const flexibleCurrencyTransferConflict = computed(
    () =>
        isPointsRedemption.value &&
        transferPartnersGuideEnabled.value &&
        program.value?.type === 'flexible_currency' &&
        hasPersistedTransfers.value
);

// Show the flexible-currency transfer option for points redemptions. When
// transfer-partners-guide is on, hide it for flexible-currency destinations
// (e.g. Chase UR) since flexible programs don't accept inbound transfers —
// unless persisted transfer rows exist (MY1-3014): those must surface as
// visible-but-flagged, never silently disappear.
const showFlexibleCurrencyTransfer = computed(() => {
    if (!isPointsRedemption.value) {
        return false;
    }

    if (
        transferPartnersGuideEnabled.value &&
        program.value?.type === 'flexible_currency' &&
        !hasPersistedTransfers.value
    ) {
        return false;
    }

    return true;
});

const isUsingCompanionPass = ref(false);

watch(isUsingCompanionPass, (newValue) => {
    activeRedemption.value.companion_pass_used = newValue;
    // For Southwest companion pass, store the companion tax amount
    if (activeRedemption.value.isSouthwestFlight) {
        if (newValue) {
            activeRedemption.value._original_taxes_fees =
                activeRedemption.value.advertised_taxes_fees;

            const totalTaxes =
                Number(activeRedemption.value.advertised_taxes_fees) || 0;
            activeRedemption.value.companion_tax_amount = (
                totalTaxes / 2
            ).toString();
        } else {
            activeRedemption.value.companion_tax_amount = null;
        }
    }
});

watch(
    () => activeRedemption.value,
    (newValue) => {
        if (newValue && newValue.uuid) {
            if (newValue.companion_pass_used === true) {
                isUsingCompanionPass.value = true;

                if (newValue.isSouthwestFlight) {
                    if (!newValue._original_taxes_fees) {
                        if (newValue.companion_tax_amount) {
                            newValue._original_taxes_fees =
                                newValue.companion_tax_amount;
                        } else {
                            newValue._original_taxes_fees = (
                                parseFloat(newValue.advertised_taxes_fees) / 2
                            ).toString();
                        }
                    }

                    const originalValue =
                        newValue._original_taxes_fees ||
                        newValue.advertised_taxes_fees ||
                        0;
                    newValue.companion_tax_amount = originalValue;

                    if (
                        parseFloat(newValue.advertised_taxes_fees) <
                        parseFloat(originalValue) * 2
                    ) {
                        newValue.advertised_taxes_fees = (
                            parseFloat(originalValue) * 2
                        ).toString();
                    }
                }
            }
        }
    },
    { immediate: true }
);

watch(
    () => activeRedemption.value?.payment_type,
    (newType, oldType) => {
        if (
            activeRedemption.value?.isSouthwestFlight &&
            isUsingCompanionPass.value
        ) {
            if (!activeRedemption.value._original_taxes_fees) {
                if (activeRedemption.value.companion_tax_amount) {
                    activeRedemption.value._original_taxes_fees =
                        activeRedemption.value.companion_tax_amount;
                } else {
                    activeRedemption.value._original_taxes_fees = (
                        parseFloat(
                            activeRedemption.value.advertised_taxes_fees
                        ) / 2
                    ).toString();
                }
            }

            const originalValue =
                activeRedemption.value._original_taxes_fees ||
                activeRedemption.value.advertised_taxes_fees ||
                0;
            activeRedemption.value.companion_tax_amount = originalValue;

            if (
                parseFloat(activeRedemption.value.advertised_taxes_fees) <
                parseFloat(originalValue) * 2
            ) {
                activeRedemption.value.advertised_taxes_fees = (
                    parseFloat(originalValue) * 2
                ).toString();
            }
        }
    }
);

watch(
    () => activeRedemption.value?.travel_segments,
    (newSegments) => {
        if (newSegments && newSegments.length > 0) {
            const isSouthwest = newSegments.some(
                (segment) =>
                    segment.travel_number &&
                    segment.travel_number.toLowerCase().startsWith('wn')
            );

            activeRedemption.value.isSouthwestFlight = isSouthwest;

            if (
                isSouthwest &&
                window.lastCompanionPassState &&
                !activeRedemption.value.companion_pass_used
            ) {
                toggleCompanionPass(true);
            } else if (!isSouthwest) {
                toggleCompanionPass(false);
            }
        }
    },
    { deep: true }
);

const toggleCompanionPass = (value) => {
    isUsingCompanionPass.value = value;
    activeRedemption.value.companion_pass_used = value;
};

const formattedCashPrice = computed(() => {
    const price = Number(activeRedemption.value.advertised_price) || 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(price);
});

const formattedTaxesPrice = computed(() => {
    const taxes = Number(activeRedemption.value.advertised_taxes_fees) || 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(taxes);
});

const formattedCompanionTax = computed(() => {
    if (
        isUsingCompanionPass.value &&
        activeRedemption.value.isSouthwestFlight
    ) {
        // Calculate half of the total taxes as the companion's portion
        const totalTaxes =
            Number(activeRedemption.value.advertised_taxes_fees) || 0;
        const companionTaxes = totalTaxes / 2;

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(companionTaxes);
    }
    return '$0.00';
});

const formattedTotalPrice = computed(() => {
    const price = Number(activeRedemption.value.advertised_price) || 0;
    const taxes = Number(activeRedemption.value.advertised_taxes_fees) || 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(price + taxes);
});

const addTransfer = () => {
    // Some redemptions arrive without a transfers list (SUPPORT-835).
    if (!Array.isArray(activeRedemption.value.transfers)) {
        activeRedemption.value.transfers = [];
    }

    activeRedemption.value.transfers.push({
        transfer_points: 0,
        transfer_bonus_percentage: 0,
        transfer_program_id: null,
    });
};

const togglePointsTransferred = (value) => {
    activeRedemption.value.flexible_points_transfer = value;
};

const updateUsedTravelPortal = (value) => {
    activeRedemption.value.usedTravelPortal = value;
};

const updateTravelPortal = (travelPortal) => {
    if (travelPortal) {
        activeRedemption.value.travel_portal_id = travelPortal.id;
        activeRedemption.value.usedTravelPortal = true;

        const updatedRedemption = { ...activeRedemption.value };
        tripsStore.activeRedemption = updatedRedemption;
    } else {
        activeRedemption.value.travel_portal_id = null;
        activeRedemption.value.travel_portal_custom_name = null;
        activeRedemption.value.usedTravelPortal = false;

        const updatedRedemption = { ...activeRedemption.value };
        tripsStore.activeRedemption = updatedRedemption;
    }
};

const updateTravelPortalName = (travelPortal) => {
    activeRedemption.value.travel_portal_custom_name =
        travelPortal.travel_portal_custom_name;
    const updatedRedemption = { ...activeRedemption.value };
    tripsStore.activeRedemption = updatedRedemption;
};

const dateConfig = ref({
    dateFormat: 'm/d/Y',
    maxDate: 'today',
});

watch(
    () => activeRedemption.value.connected_account_id,
    (val) => {
        tripsStore.setConnectedAccount(val);
    },
    { deep: true }
);

watch(
    [
        () => activeRedemption.value.payment_type,
        () => activeRedemption.value.advertised_price,
        () => activeRedemption.value.advertised_taxes_fees,
    ],
    ([paymentType, price, taxesFees]) => {
        if (paymentType === 'cash') {
            const numPrice = Number(price) || 0;
            const numTaxesFees = Number(taxesFees) || 0;

            activeRedemption.value.payment_cash_amount = numPrice;
            activeRedemption.value.payment_taxes_fees = numTaxesFees;
        }
    },
    { immediate: true }
);

onMounted(() => {});

// If the selected Points currency is itself a flexible currency (e.g., Chase UR),
// disable the transfer toggle since flexible programs generally don't accept
// inbound transfers.
// MY1-3014: only a user-driven currency change within one editing session may
// reset the toggle. The uuid guard distinguishes "modal opened / swapped to
// another redemption" (initialization — must never rewrite saved data) from
// "the user re-picked the currency on the same redemption". No
// { immediate: true }: firing on mount retroactively flipped the flag on
// saved redemptions, and the next save hard-deleted their transfer rows.
watch(
    () => [activeRedemption.value?.uuid, program.value?.type],
    ([uuid, newType], [previousUuid, previousType]) => {
        // An unresolved previousType means the async programs list only just
        // loaded for an already-open redemption — initialization, not a
        // user-driven currency change.
        if (
            uuid !== previousUuid ||
            newType === previousType ||
            !previousType
        ) {
            return;
        }

        if (
            transferPartnersGuideEnabled.value &&
            newType === 'flexible_currency'
        ) {
            activeRedemption.value.flexible_points_transfer = false;
        }
    }
);
</script>
